/**
 * Application route definitions with lazy-loaded pages.
 *
 * Foundation routes are statically defined. Module routes are
 * dynamically discovered from src/modules/SLUG/routes.tsx using
 * Vite glob imports. A module's routes are only mounted when the
 * module slug appears in the current navigation items (i.e., the
 * module is installed and active for the tenant). Uninstalled
 * module paths render a "Module not available" fallback.
 *
 * Cross-References:
 *   - S-MOD.11: Frontend dynamic navigation
 *   - Setup Guide: planning_deliverables/04-implementation/setup-guide.md
 *   - UI Screens: planning_deliverables/03-specifications/ui/
 */
import React, { lazy, Suspense, type ComponentType } from 'react';
import { Routes, Route, Navigate, Outlet } from 'react-router-dom';
import { AuthGuard, AdminGuard, PermissionGuard } from './guards';
import { Layout } from '@/components/shell/Layout';
import { useNavigation } from '@/hooks/useNavigation';
import { useIntranetSites } from '@/hooks/useIntranetSites';

// Lazy-loaded pages — code-split per route
const Login = lazy(() => import('@/pages/auth/Login'));
const Callback = lazy(() => import('@/pages/auth/Callback'));
const SessionSelector = lazy(() => import('@/pages/auth/SessionSelector'));
const Dashboard = lazy(() => import('@/pages/dashboard/Dashboard'));
const UserDirectory = lazy(() => import('@/pages/directory/UserDirectory'));
const UserProfile = lazy(() => import('@/pages/directory/UserProfile'));
// ACT-1 — My Last Activity + the audited admin view of a user's activity.
const MyLastActivity = lazy(() => import('@/pages/activity/MyLastActivity'));
const AdminUserActivity = lazy(() =>
  import('@/pages/activity/MyLastActivity').then((m) => ({ default: m.AdminUserActivity })),
);
const AdminUsers = lazy(() => import('@/pages/admin/AdminUsers'));
const AdminDepartments = lazy(() => import('@/pages/admin/AdminDepartments'));
const AdminLocations = lazy(() => import('@/pages/admin/AdminLocations'));
const Integrations = lazy(() => import('@/pages/integrations/Integrations'));
const AdminRoles = lazy(() => import('@/pages/admin/AdminRoles'));
const AdminPermissionSets = lazy(() => import('@/pages/admin/AdminPermissionSets'));
const AdminTenantSettings = lazy(() => import('@/pages/admin/AdminTenantSettings'));
const AdminAuditLog = lazy(() => import('@/pages/admin/AdminAuditLog'));
const AdminAccessReviews = lazy(() => import('@/pages/admin/AdminAccessReviews'));
const SensitiveAccessLog = lazy(() => import('@/pages/admin/SensitiveAccessLog'));
const AdminOutboundDispatches = lazy(() => import('@/pages/admin/AdminOutboundDispatches'));
const AdminBilling = lazy(() => import('@/pages/admin/AdminBilling'));
// INT-10 (2026-05-28): legacy AdminIntegrations + /admin/integrations alias
// retired. /integrations is the canonical surface; /admin/integrations 404s.
const AdminModules = lazy(() => import('@/pages/admin/AdminModules'));
const AdminCrons = lazy(() => import('@/pages/admin/AdminCrons'));
const AdminOperationalDashboard = lazy(
  () => import('@/pages/admin/AdminOperationalDashboard'),
);
const AdminCMGrants = lazy(() => import('@/pages/admin/AdminCMGrants'));
// DASH cycle — news + dashboard region admin
const AdminNews = lazy(() => import('@/pages/admin/AdminNews'));
const AdminDashboardRegions = lazy(() => import('@/pages/admin/AdminDashboardRegions'));
const AdminSfQuotas = lazy(() => import('@/pages/admin/AdminSfQuotas'));
const NewsIndex = lazy(() => import('@/pages/news/NewsIndex'));
const NewsRead = lazy(() => import('@/pages/news/NewsRead'));
const InternalLogin = lazy(() => import('@/pages/auth/InternalLogin'));
const OnboardingWizard = lazy(() => import('@/pages/auth/OnboardingWizard'));
const NotFound = lazy(() => import('@/pages/NotFound'));
const ModuleNotAvailable = lazy(() => import('@/pages/ModuleNotAvailable'));
// K09: no product navigation entry; production builds do not register this route.
const UiKitGallery = import.meta.env.DEV
  ? lazy(() => import('@/pages/dev/UiKitGallery'))
  : null;

/* ── Dynamic module route discovery ────────────────────────── */

interface ModuleRouteDef {
  path: string;
  element: ComponentType;
}

interface ModuleRouteConfig {
  slug: string;
  routes: ModuleRouteDef[];
}

/**
 * Eagerly import all modules/SLUG/routes.tsx files at build time.
 * Vite resolves the glob and returns a record of path to module.
 */
const moduleRouteFiles = import.meta.glob<{
  slug: string;
  routes: ModuleRouteDef[];
}>('../modules/*/routes.tsx', { eager: true });

/** Flatten glob results into an ordered array of module configs. */
const MODULE_CONFIGS: ModuleRouteConfig[] = Object.values(moduleRouteFiles).map(
  (mod) => ({ slug: mod.slug, routes: mod.routes }),
);

/** All known module slugs (available on disk regardless of install status). */
const ALL_MODULE_SLUGS = new Set(MODULE_CONFIGS.map((m) => m.slug));

/* ── Layout wrapper ────────────────────────────────────────── */

/** Layout route — renders sidebar + header around all authenticated pages. */
function AuthenticatedLayout() {
  return (
    <Layout>
      {/* Pages load inside the shell, so the sidebar and header stay
          mounted (and the nav keeps its scroll) while a chunk loads. */}
      <Suspense fallback={<PageLoader />}>
        <Outlet />
      </Suspense>
    </Layout>
  );
}

function PageLoader() {
  return (
    <div className="page-loader">
      <div className="page-loader__spinner" />
    </div>
  );
}

/* ── Module route renderer ─────────────────────────────────── */

/** Derive installed module slugs from navigation items + CM's page
 * tree. Sprint CM-17-W2 retired CM's nav items (the section now
 * surfaces purely via the page tree from /pages/roots); if root
 * pages exist we infer CM is installed for the tenant. */
function useInstalledModuleSlugs(): Set<string> {
  const { items } = useNavigation();
  const { sites: intranetSites } = useIntranetSites();
  const slugs = new Set<string>();
  for (const item of items) {
    if (item.section !== 'modules' && item.section !== 'admin') continue;
    const match = item.path.match(/^\/modules\/([^/]+)/);
    if (match) slugs.add(match[1]);
  }
  if (intranetSites.length > 0) slugs.add('cm');
  return slugs;
}

/**
 * Build module Route elements conditionally:
 * - Installed modules get their lazy-loaded pages
 * - Known but uninstalled modules get ModuleNotAvailable fallback
 * - Unknown paths fall through to NotFound
 *
 * Returns a flat array of Route elements (not a component) so that
 * the result can be spread directly inside a Routes block without
 * violating React Router v6 child-type constraints.
 */
function useModuleRoutes(installedSlugs: Set<string>): React.ReactElement[] {
  return MODULE_CONFIGS.flatMap((config) => {
    if (installedSlugs.has(config.slug)) {
      return config.routes.map((route) => {
        const Elem = route.element;
        return (
          <Route
            key={`${config.slug}/${route.path}`}
            path={`${config.slug}/${route.path}`}
            element={<Elem />}
          />
        );
      });
    }

    // Module exists on disk but not installed — fallback
    return [
      <Route
        key={`${config.slug}-unavailable`}
        path={`${config.slug}/*`}
        element={<ModuleNotAvailable />}
      />,
    ];
  });
}

/* ── Main router ───────────────────────────────────────────── */

/**
 * Wrapper component that resolves module routes lazily.
 * Placed inside an AuthGuard so useNavigation (and the API call it
 * makes) only fires for authenticated users — avoiding 401 loops
 * on public routes like /login.
 */
function AuthenticatedModuleRoutes() {
  const installedSlugs = useInstalledModuleSlugs();
  const moduleRoutes = useModuleRoutes(installedSlugs);
  return <Routes>{moduleRoutes}</Routes>;
}

export function AppRoutes() {
  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        {/* Public routes */}
        <Route path="/login" element={<Login />} />
        <Route path="/internal/login" element={<InternalLogin />} />
        <Route path="/auth/callback" element={<Callback />} />
        <Route path="/select-session" element={<SessionSelector />} />
        <Route path="/onboarding" element={<OnboardingWizard />} />

        {/* All authenticated routes share Layout (sidebar + header) */}
        <Route element={<AuthGuard />}>
          <Route element={<AuthenticatedLayout />}>
            <Route path="/" element={<Navigate to="/dashboard" replace />} />
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/directory" element={<UserDirectory />} />
            <Route path="/profile" element={<UserProfile />} />
            <Route path="/users/:id" element={<UserProfile />} />
            <Route path="/activity" element={<MyLastActivity />} />
            <Route element={<PermissionGuard permission="employment.read_sensitive.org" />}>
              <Route path="/admin/sensitive-access-log" element={<SensitiveAccessLog />} />
            </Route>
            <Route element={<PermissionGuard permission="user-activity.read.org" />}>
              <Route path="/admin/users/:userId/activity" element={<AdminUserActivity />} />
            </Route>
            {/* Sprint INT-2 TKT-4 — top-level Integrations (Option A).
                Anyone signed in can view; admin-only "Tenant integrations"
                section conditionally renders inside the page. */}
            <Route path="/integrations" element={<Integrations />} />
            {UiKitGallery && <Route path="/dev/ui-kit" element={<UiKitGallery />} />}
          </Route>
        </Route>

        {/* Admin routes — also share Layout */}
        <Route element={<AdminGuard />}>
          <Route element={<AuthenticatedLayout />}>
            <Route path="/admin/users" element={<AdminUsers />} />
            <Route path="/admin/departments" element={<AdminDepartments />} />
            <Route path="/admin/locations" element={<AdminLocations />} />
            <Route path="/admin/roles" element={<AdminRoles />} />
            <Route path="/admin/permission-sets" element={<AdminPermissionSets />} />
            <Route path="/admin/settings" element={<AdminTenantSettings />} />
            <Route path="/admin/audit" element={<AdminAuditLog />} />
            <Route path="/admin/access-reviews" element={<AdminAccessReviews />} />
            <Route path="/admin/outbound-dispatches" element={<AdminOutboundDispatches />} />
            <Route path="/admin/billing" element={<AdminBilling />} />
            {/* INT-10 (2026-05-28): /admin/integrations alias retired —
                404 per owner directive (Option A's redirect was rejected). */}
            <Route path="/admin/modules" element={<AdminModules />} />
            <Route path="/admin/crons" element={<AdminCrons />} />
            {/* Sprint INFRA-OBS-1 */}
            <Route path="/admin/operational" element={<AdminOperationalDashboard />} />
            {/* Sprint CM-AUD-5 */}
            <Route path="/admin/cm/grants" element={<AdminCMGrants />} />
            {/* DASH cycle */}
            <Route path="/admin/news" element={<AdminNews />} />
            <Route path="/admin/dashboard-regions" element={<AdminDashboardRegions />} />
            {/* CW-SF-R2 — sales quota admin */}
            <Route path="/admin/sf/quotas" element={<AdminSfQuotas />} />
          </Route>
        </Route>

        {/* DASH-2 — public news read pages, gated only on auth */}
        <Route element={<AuthGuard />}>
          <Route element={<AuthenticatedLayout />}>
            <Route path="/news" element={<NewsIndex />} />
            <Route path="/news/:id" element={<NewsRead />} />
          </Route>
        </Route>

        {/* Dynamic module routes — inside AuthGuard so useNavigation only fires for logged-in users */}
        <Route element={<AuthGuard />}>
          <Route element={<AuthenticatedLayout />}>
            <Route path="/modules/*" element={<AuthenticatedModuleRoutes />} />
          </Route>
        </Route>

        {/* Catch-all */}
        <Route path="*" element={<NotFound />} />
      </Routes>
    </Suspense>
  );
}
